import React, { useState, useMemo } from 'react';
import {
  Card, Row, Col, Table, Badge, Button, Form, Spinner, Alert,
  Modal, Pagination, InputGroup
} from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_SALES,
  CREATE_SALE,
  UPDATE_SALE,
  UPDATE_SALE_STATUS,
  DELETE_SALE,
  GET_INVOICES,
  GET_EXPENSES
} from '../../graphql/operations';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SaleItem {
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
}

export interface Sale {
  id: string;
  saleId: string;
  customerName: string;
  customerContact?: string;
  customerEmail?: string;
  category?: string;
  items: SaleItem[];
  totalAmount: number;
  paidAmount?: number;
  balanceDue?: number;
  paymentStatus?: string;
  paymentMode?: string;
  invoiceRef?: string;
  date: string;
  notes?: string;
  source?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface SalesTrackerProps {
  triggerToast?: (message: string, variant?: 'success' | 'danger' | 'warning' | 'info') => void;
}

const CATEGORIES = [
  'General Sales',
  'Digital Printing',
  'Banner Printing',
  'Offset Printing',
  'Stickers & Labels',
  'Visiting Cards',
  'Signage & Boards',
  'Corporate Gifts',
  'Binding & Lamination',
  'Other',
];

const PAYMENT_MODES = ['UPI', 'Cash', 'Bank Transfer', 'Card', 'Cheque', 'Credit', 'Other'];
const STATUSES = ['Paid', 'Partially Paid', 'Unpaid', 'Cancelled'];

const EMPTY_ITEM: SaleItem = { name: '', quantity: 1, unit: 'pcs', unitPrice: 0, totalPrice: 0 };

const statusBadgeVariant = (status?: string) => {
  switch (status) {
    case 'Paid': return 'success';
    case 'Partially Paid': return 'warning';
    case 'Unpaid': return 'danger';
    case 'Cancelled': return 'secondary';
    default: return 'info';
  }
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Empty Form State ─────────────────────────────────────────────────────────

const emptyForm = () => ({
  customerName: '',
  customerContact: '',
  customerEmail: '',
  category: 'General Sales',
  date: new Date().toISOString().slice(0, 10),
  paymentStatus: 'Paid',
  paymentMode: 'UPI',
  invoiceRef: '',
  paidAmount: 0,
  notes: '',
  source: 'Manual',
  items: [{ ...EMPTY_ITEM, name: 'Printing Services', unitPrice: 0, totalPrice: 0 }],
});

const SalesTracker: React.FC<SalesTrackerProps> = ({ triggerToast }) => {
  const [activeTab, setActiveTab] = useState<'records' | 'analytics'>('records');

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Add / Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Quick Status Modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusSale, setStatusSale] = useState<Sale | null>(null);
  const [quickStatus, setQuickStatus] = useState('');
  const [quickPayMode, setQuickPayMode] = useState('');
  const [quickPaid, setQuickPaid] = useState(0);

  // Delete Confirm
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // GraphQL Operations
  const { data, loading, error, refetch } = useQuery(GET_SALES, {
    fetchPolicy: 'network-only',
  });

  const { data: invoicesData } = useQuery(GET_INVOICES, {
    fetchPolicy: 'network-only',
  });

  const { data: expensesData } = useQuery(GET_EXPENSES, {
    fetchPolicy: 'network-only',
  });

  const [createSale] = useMutation(CREATE_SALE, {
    refetchQueries: [{ query: GET_SALES }],
  });
  const [updateSale] = useMutation(UPDATE_SALE, {
    refetchQueries: [{ query: GET_SALES }],
  });
  const [updateSaleStatus] = useMutation(UPDATE_SALE_STATUS, {
    refetchQueries: [{ query: GET_SALES }],
  });
  const [deleteSaleMutation] = useMutation(DELETE_SALE, {
    refetchQueries: [{ query: GET_SALES }],
  });

  const notify = (msg: string, variant: 'success' | 'danger' | 'warning' | 'info' = 'success') => {
    if (triggerToast) triggerToast(msg, variant);
  };

  const allSales: Sale[] = data?.sales || [];

  // ─── Filtered Data ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return allSales.filter(s => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        s.customerName.toLowerCase().includes(q) ||
        s.saleId.toLowerCase().includes(q) ||
        (s.customerContact || '').toLowerCase().includes(q) ||
        (s.category || '').toLowerCase().includes(q) ||
        (s.invoiceRef || '').toLowerCase().includes(q) ||
        (s.notes || '').toLowerCase().includes(q);

      const matchStatus = statusFilter === 'all' || s.paymentStatus === statusFilter;
      const matchCategory = categoryFilter === 'all' || s.category === categoryFilter;
      const matchSource = sourceFilter === 'all' || (s.source || 'Manual') === sourceFilter;
      const matchFrom = !dateFrom || s.date >= dateFrom;
      const matchTo = !dateTo || s.date <= dateTo;

      return matchSearch && matchStatus && matchCategory && matchSource && matchFrom && matchTo;
    });
  }, [allSales, search, statusFilter, categoryFilter, sourceFilter, dateFrom, dateTo]);

  // Paginated
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginatedSales = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  // ─── Summary Metrics ────────────────────────────────────────────────────────
  const metrics = useMemo(() => {
    const totalRevenue = allSales.reduce((acc, s) => acc + (s.totalAmount || 0), 0);
    const totalPaid = allSales.reduce((acc, s) => acc + (s.paidAmount || 0), 0);
    const totalBalance = allSales.reduce((acc, s) => acc + (s.balanceDue || 0), 0);
    const statementSales = allSales.filter(s => s.source === 'Bank Statement');
    const statementTotal = statementSales.reduce((acc, s) => acc + (s.totalAmount || 0), 0);
    const manualSales = allSales.filter(s => s.source !== 'Bank Statement');
    const manualTotal = manualSales.reduce((acc, s) => acc + (s.totalAmount || 0), 0);

    return {
      totalRevenue,
      totalPaid,
      totalBalance,
      totalCount: allSales.length,
      statementCount: statementSales.length,
      statementTotal,
      manualCount: manualSales.length,
      manualTotal,
    };
  }, [allSales]);

  // ─── Form Handlers ──────────────────────────────────────────────────────────
  const calculateTotal = (items: SaleItem[]) =>
    items.reduce((acc, it) => acc + (Number(it.totalPrice) || 0), 0);

  const handleItemChange = (index: number, field: keyof SaleItem, value: any) => {
    const updated = form.items.map((it, i) => {
      if (i !== index) return it;
      const next = { ...it, [field]: value };
      if (field === 'quantity' || field === 'unitPrice') {
        const q = field === 'quantity' ? Number(value) : it.quantity;
        const p = field === 'unitPrice' ? Number(value) : it.unitPrice;
        next.totalPrice = Math.max(0, q * p);
      }
      return next;
    });
    const total = calculateTotal(updated);
    setForm(prev => ({
      ...prev,
      items: updated,
      paidAmount: prev.paymentStatus === 'Paid' ? total : prev.paidAmount,
    }));
  };

  const addItem = () =>
    setForm(prev => ({ ...prev, items: [...prev.items, { ...EMPTY_ITEM }] }));

  const removeItem = (idx: number) => {
    if (form.items.length <= 1) return;
    const updated = form.items.filter((_, i) => i !== idx);
    const total = calculateTotal(updated);
    setForm(prev => ({
      ...prev,
      items: updated,
      paidAmount: prev.paymentStatus === 'Paid' ? total : prev.paidAmount,
    }));
  };

  const openAddModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setForm(emptyForm());
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (sale: Sale) => {
    setIsEditing(true);
    setEditingId(sale.id);
    setForm({
      customerName: sale.customerName,
      customerContact: sale.customerContact || '',
      customerEmail: sale.customerEmail || '',
      category: sale.category || 'General Sales',
      date: sale.date,
      paymentStatus: sale.paymentStatus || 'Paid',
      paymentMode: sale.paymentMode || 'UPI',
      invoiceRef: sale.invoiceRef || '',
      paidAmount: sale.paidAmount || 0,
      notes: sale.notes || '',
      source: sale.source || 'Manual',
      items: sale.items?.length
        ? sale.items.map(it => ({ ...it }))
        : [{ ...EMPTY_ITEM, name: 'Printing Services', unitPrice: sale.totalAmount, totalPrice: sale.totalAmount }],
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customerName.trim()) {
      setFormError('Customer name is required.');
      return;
    }

    const totalAmount = calculateTotal(form.items);
    if (totalAmount <= 0) {
      setFormError('Total sale amount must be greater than zero. Please add items with prices.');
      return;
    }

    setSaving(true);
    setFormError('');

    const paid = Number(form.paidAmount) || 0;
    const balance = Math.max(0, totalAmount - paid);

    const input = {
      customerName: form.customerName.trim(),
      customerContact: form.customerContact.trim(),
      customerEmail: form.customerEmail.trim(),
      category: form.category,
      items: form.items.map(it => ({
        name: it.name.trim() || 'Custom Item',
        quantity: Number(it.quantity) || 1,
        unit: it.unit || 'pcs',
        unitPrice: Number(it.unitPrice) || 0,
        totalPrice: Number(it.totalPrice) || 0,
      })),
      totalAmount,
      paidAmount: paid,
      balanceDue: balance,
      paymentStatus: form.paymentStatus,
      paymentMode: form.paymentMode,
      invoiceRef: form.invoiceRef.trim(),
      date: form.date,
      notes: form.notes.trim(),
      source: form.source || 'Manual',
    };

    try {
      if (isEditing && editingId) {
        const res = await updateSale({ variables: { id: editingId, input } });
        if (res.data?.updateSale?.success) {
          notify('Sale updated successfully!');
          setShowModal(false);
        } else {
          setFormError(res.data?.updateSale?.message || 'Failed to update sale.');
        }
      } else {
        const res = await createSale({ variables: { input } });
        if (res.data?.createSale?.success) {
          notify('Sale recorded successfully!');
          setShowModal(false);
        } else {
          setFormError(res.data?.createSale?.message || 'Failed to record sale.');
        }
      }
    } catch (err: any) {
      setFormError(err.message || 'Server error occurred.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Quick Payment Status Update ───────────────────────────────────────────
  const openStatusModal = (sale: Sale) => {
    setStatusSale(sale);
    setQuickStatus(sale.paymentStatus || 'Paid');
    setQuickPayMode(sale.paymentMode || 'UPI');
    setQuickPaid(sale.paidAmount || sale.totalAmount);
    setShowStatusModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!statusSale) return;
    try {
      const res = await updateSaleStatus({
        variables: {
          id: statusSale.id,
          paymentStatus: quickStatus,
          paymentMode: quickPayMode,
          paidAmount: Number(quickPaid),
        },
      });
      if (res.data?.updateSaleStatus?.success) {
        notify('Sale payment status updated!');
        setShowStatusModal(false);
      }
    } catch (err: any) {
      notify(err.message || 'Failed to update status', 'danger');
    }
  };

  // ─── Delete Handler ─────────────────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      const res = await deleteSaleMutation({ variables: { id } });
      if (res.data?.deleteSale?.success) {
        notify('Sale record deleted successfully.');
        setConfirmDeleteId(null);
      } else {
        notify(res.data?.deleteSale?.message || 'Failed to delete sale', 'danger');
      }
    } catch (err: any) {
      notify(err.message || 'Error deleting sale', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  // ─── Export Features ────────────────────────────────────────────────────────
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      filtered.map(s => ({
        'Sale ID': s.saleId,
        Date: s.date,
        Customer: s.customerName,
        Contact: s.customerContact || '',
        Category: s.category || '',
        'Total (Rs)': s.totalAmount,
        'Paid (Rs)': s.paidAmount || 0,
        'Balance (Rs)': s.balanceDue || 0,
        Status: s.paymentStatus || '',
        Mode: s.paymentMode || '',
        Source: s.source || 'Manual',
        Ref: s.invoiceRef || '',
        Notes: s.notes || '',
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales');
    XLSX.writeFile(wb, `sales_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Sales Report - Prathitha Digital Printers', 14, 18);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 25);
    doc.text(`Total Revenue: Rs. ${metrics.totalRevenue.toFixed(2)} | Collected: Rs. ${metrics.totalPaid.toFixed(2)} | Due: Rs. ${metrics.totalBalance.toFixed(2)}`, 14, 31);

    autoTable(doc, {
      startY: 37,
      head: [['Sale ID', 'Date', 'Customer', 'Category', 'Total (Rs)', 'Paid (Rs)', 'Status', 'Source']],
      body: filtered.map(s => [
        s.saleId,
        formatDate(s.date),
        s.customerName,
        s.category || 'General',
        `Rs. ${s.totalAmount.toFixed(2)}`,
        `Rs. ${(s.paidAmount || 0).toFixed(2)}`,
        s.paymentStatus || 'Paid',
        s.source || 'Manual',
      ]),
      theme: 'grid',
      headStyles: { fillColor: [16, 185, 129] },
      styles: { fontSize: 8 },
    });
    doc.save(`sales_report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="sales-tracker-module">
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div>
          <h4 className="mb-0 fw-bold" style={{ color: '#0f172a' }}>
            <i className="fas fa-chart-line me-2" style={{ color: '#10b981' }} />
            Sales Module
          </h4>
          <p className="text-muted mb-0 small mt-1">
            Track customer sales, record manual walk-in orders, and view sales pushed from the Bank Statement Analyzer.
          </p>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="success"
            size="sm"
            className="rounded-pill px-3 shadow-sm fw-semibold"
            style={{ background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none' }}
            onClick={openAddModal}
          >
            <i className="fas fa-plus me-1" />
            Add Sale Manually
          </Button>

          <Button variant="outline-success" size="sm" className="rounded-pill px-3" onClick={exportExcel}>
            <i className="fas fa-file-excel me-1" />
            Excel
          </Button>

          <Button variant="outline-danger" size="sm" className="rounded-pill px-3" onClick={exportPDF}>
            <i className="fas fa-file-pdf me-1" />
            PDF
          </Button>

          <Button variant="outline-secondary" size="sm" className="rounded-pill px-3" onClick={() => refetch()}>
            <i className="fas fa-sync-alt me-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* ─── Summary Cards ────────────────────────────────────────────────── */}
      <Row className="g-3 mb-4">
        {/* Total Revenue */}
        <Col lg={3} sm={6}>
          <Card
            className="border-0 h-100 shadow-sm"
            style={{
              borderRadius: '18px',
              background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
              border: '1px solid #a7f3d0',
            }}
          >
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="small fw-bold text-uppercase" style={{ color: '#047857', letterSpacing: '0.5px' }}>
                  Total Sales Revenue
                </span>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                  }}
                >
                  <i className="fas fa-rupee-sign" />
                </div>
              </div>
              <h3 className="fw-bold mb-1" style={{ color: '#064e3b' }}>
                ₹{metrics.totalRevenue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </h3>
              <div className="text-muted small">
                <strong>{metrics.totalCount}</strong> total sale transactions
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Collected / Paid */}
        <Col lg={3} sm={6}>
          <Card
            className="border-0 h-100 shadow-sm"
            style={{
              borderRadius: '18px',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              border: '1px solid #bfdbfe',
            }}
          >
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="small fw-bold text-uppercase" style={{ color: '#1d4ed8', letterSpacing: '0.5px' }}>
                  Paid / Collected
                </span>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: '#3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                  }}
                >
                  <i className="fas fa-check-circle" />
                </div>
              </div>
              <h3 className="fw-bold mb-1" style={{ color: '#1e3a8a' }}>
                ₹{metrics.totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </h3>
              <div className="text-muted small">
                {metrics.totalRevenue > 0
                  ? `${((metrics.totalPaid / metrics.totalRevenue) * 100).toFixed(1)}% recovery rate`
                  : '0% collected'}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Balance Due */}
        <Col lg={3} sm={6}>
          <Card
            className="border-0 h-100 shadow-sm"
            style={{
              borderRadius: '18px',
              background: 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)',
              border: '1px solid #fecdd3',
            }}
          >
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="small fw-bold text-uppercase" style={{ color: '#be123c', letterSpacing: '0.5px' }}>
                  Balance Due
                </span>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                  }}
                >
                  <i className="fas fa-clock" />
                </div>
              </div>
              <h3 className="fw-bold mb-1" style={{ color: '#881337' }}>
                ₹{metrics.totalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </h3>
              <div className="text-muted small">
                Pending receivables from customers
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Bank Statement Pushed */}
        <Col lg={3} sm={6}>
          <Card
            className="border-0 h-100 shadow-sm"
            style={{
              borderRadius: '18px',
              background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
              border: '1px solid #e9d5ff',
            }}
          >
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="small fw-bold text-uppercase" style={{ color: '#7e22ce', letterSpacing: '0.5px' }}>
                  From Bank Statement
                </span>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: '#9333ea',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                  }}
                >
                  <i className="fas fa-university" />
                </div>
              </div>
              <h3 className="fw-bold mb-1" style={{ color: '#581c87' }}>
                ₹{metrics.statementTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </h3>
              <div className="text-muted small">
                <strong>{metrics.statementCount}</strong> pushed via Statement Analyzer
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ─── Filters & Search Toolbar ────────────────────────────────────── */}
      <Card className="border-0 shadow-sm rounded-4 mb-3 bg-white">
        <Card.Body className="p-3">
          <Row className="g-2 align-items-center">
            {/* Search */}
            <Col md={3} sm={12}>
              <InputGroup size="sm">
                <InputGroup.Text style={{ background: '#f8fafc', borderRight: 'none' }}>
                  <i className="fas fa-search text-muted" />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search customer, ID, notes..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                  style={{ borderLeft: 'none', background: '#f8fafc' }}
                />
              </InputGroup>
            </Col>

            {/* Status Filter */}
            <Col md={2} sm={6}>
              <Form.Select
                size="sm"
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                style={{ background: '#f8fafc' }}
              >
                <option value="all">All Statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </Form.Select>
            </Col>

            {/* Category Filter */}
            <Col md={2} sm={6}>
              <Form.Select
                size="sm"
                value={categoryFilter}
                onChange={e => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
                style={{ background: '#f8fafc' }}
              >
                <option value="all">All Categories</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Col>

            {/* Source Filter */}
            <Col md={2} sm={6}>
              <Form.Select
                size="sm"
                value={sourceFilter}
                onChange={e => { setSourceFilter(e.target.value); setCurrentPage(1); }}
                style={{ background: '#f8fafc' }}
              >
                <option value="all">All Sources</option>
                <option value="Manual">Manual Entry</option>
                <option value="Bank Statement">Bank Statement</option>
              </Form.Select>
            </Col>

            {/* Date Range */}
            <Col md={3} sm={6}>
              <div className="d-flex align-items-center gap-1">
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateFrom}
                  onChange={e => { setDateFrom(e.target.value); setCurrentPage(1); }}
                  style={{ background: '#f8fafc', fontSize: '0.78rem' }}
                />
                <span className="text-muted small">to</span>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateTo}
                  onChange={e => { setDateTo(e.target.value); setCurrentPage(1); }}
                  style={{ background: '#f8fafc', fontSize: '0.78rem' }}
                />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ─── Sales Records Table ─────────────────────────────────────────── */}
      <div className="table-responsive rounded-4 shadow-sm border bg-white mb-3">
        <Table hover className="mb-0 align-middle" style={{ fontSize: '0.84rem' }}>
          <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
            <tr>
              <th className="py-3 px-3" style={{ width: '130px' }}>Sale ID</th>
              <th className="py-3 px-3" style={{ width: '110px' }}>Date</th>
              <th className="py-3 px-3" style={{ minWidth: '180px' }}>Customer</th>
              <th className="py-3 px-3" style={{ width: '130px' }}>Category</th>
              <th className="py-3 px-3 text-end" style={{ width: '110px' }}>Total</th>
              <th className="py-3 px-3 text-end" style={{ width: '110px' }}>Paid</th>
              <th className="py-3 px-3 text-end" style={{ width: '110px' }}>Balance</th>
              <th className="py-3 px-3 text-center" style={{ width: '110px' }}>Status</th>
              <th className="py-3 px-3 text-center" style={{ width: '110px' }}>Source</th>
              <th className="py-3 px-3 text-center" style={{ width: '130px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="text-center py-5">
                  <Spinner animation="border" variant="success" size="sm" className="me-2" />
                  Loading sales records...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center py-5 text-muted">
                  <i className="fas fa-receipt fa-2x mb-2 d-block text-muted opacity-50" />
                  No sales found. Click <strong>"Add Sale Manually"</strong> or push deposits from the{' '}
                  <strong>Bank Statement Analyzer</strong>.
                </td>
              </tr>
            ) : (
              paginatedSales.map(sale => (
                <tr key={sale.id}>
                  {/* Sale ID */}
                  <td className="px-3">
                    <span className="fw-bold text-dark" style={{ fontSize: '0.82rem' }}>
                      {sale.saleId}
                    </span>
                    {sale.invoiceRef && (
                      <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                        Ref: {sale.invoiceRef}
                      </div>
                    )}
                  </td>

                  {/* Date */}
                  <td className="px-3 text-nowrap text-muted" style={{ fontSize: '0.8rem' }}>
                    {formatDate(sale.date)}
                  </td>

                  {/* Customer */}
                  <td className="px-3">
                    <div className="fw-bold" style={{ color: '#0f172a' }}>
                      {sale.customerName}
                    </div>
                    {sale.customerContact && (
                      <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                        <i className="fas fa-phone-alt me-1" style={{ fontSize: '0.65rem' }} />
                        {sale.customerContact}
                      </div>
                    )}
                  </td>

                  {/* Category */}
                  <td className="px-3">
                    <Badge
                      bg="light"
                      text="dark"
                      className="border rounded-pill px-2 py-1 fw-normal"
                      style={{ fontSize: '0.74rem' }}
                    >
                      {sale.category || 'General'}
                    </Badge>
                  </td>

                  {/* Total */}
                  <td className="px-3 text-end fw-bold" style={{ color: '#0f172a', fontSize: '0.88rem' }}>
                    ₹{sale.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>

                  {/* Paid */}
                  <td className="px-3 text-end fw-semibold" style={{ color: '#16a34a', fontSize: '0.85rem' }}>
                    ₹{(sale.paidAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>

                  {/* Balance */}
                  <td className="px-3 text-end">
                    {(sale.balanceDue || 0) > 0 ? (
                      <span className="fw-bold text-danger" style={{ fontSize: '0.85rem' }}>
                        ₹{(sale.balanceDue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    ) : (
                      <span className="text-muted small">Paid</span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-3 text-center">
                    <Badge
                      bg={statusBadgeVariant(sale.paymentStatus)}
                      className="rounded-pill px-2 py-1 fw-semibold cursor-pointer"
                      style={{ fontSize: '0.72rem', cursor: 'pointer' }}
                      onClick={() => openStatusModal(sale)}
                      title="Click to update payment status"
                    >
                      {sale.paymentStatus || 'Paid'}
                    </Badge>
                    {sale.paymentMode && (
                      <div className="text-muted" style={{ fontSize: '0.68rem', marginTop: '2px' }}>
                        {sale.paymentMode}
                      </div>
                    )}
                  </td>

                  {/* Source */}
                  <td className="px-3 text-center">
                    {sale.source === 'Bank Statement' ? (
                      <Badge bg="purple" className="rounded-pill px-2 py-1" style={{ background: '#7e22ce', color: '#fff', fontSize: '0.68rem' }}>
                        <i className="fas fa-university me-1" />
                        Statement
                      </Badge>
                    ) : (
                      <Badge bg="secondary" className="rounded-pill px-2 py-1" style={{ fontSize: '0.68rem' }}>
                        <i className="fas fa-keyboard me-1" />
                        Manual
                      </Badge>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-3 text-center">
                    <div className="d-flex align-items-center justify-content-center gap-1">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="rounded-circle p-1"
                        style={{ width: '28px', height: '28px', lineHeight: 1 }}
                        onClick={() => openEditModal(sale)}
                        title="Edit Sale"
                      >
                        <i className="fas fa-pen" style={{ fontSize: '0.72rem' }} />
                      </Button>

                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="rounded-circle p-1"
                        style={{ width: '28px', height: '28px', lineHeight: 1 }}
                        onClick={() => setConfirmDeleteId(sale.id)}
                        title="Delete Sale"
                      >
                        <i className="fas fa-trash" style={{ fontSize: '0.72rem' }} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* ─── Pagination ──────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <span className="text-muted small">
            Showing {paginatedSales.length} of {filtered.length} sales
          </span>
          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum = currentPage - 2 + i;
              if (pageNum < 1) pageNum = 1 + i;
              if (pageNum > totalPages) return null;
              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === currentPage}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              );
            })}
            <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}

      {/* ─── Add / Edit Sale Modal ───────────────────────────────────────── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold" style={{ fontSize: '1.1rem' }}>
            <i className={`fas fa-${isEditing ? 'edit' : 'plus-circle'} text-success me-2`} />
            {isEditing ? 'Edit Sale Record' : 'Record New Sale'}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSave}>
          <Modal.Body className="p-4">
            {formError && (
              <Alert variant="danger" className="py-2 small">
                <i className="fas fa-exclamation-circle me-1" />
                {formError}
              </Alert>
            )}

            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    Customer Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. Ramesh Kumar / Sree Agencies"
                    value={form.customerName}
                    onChange={e => setForm({ ...form, customerName: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Contact Phone / Email</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. +91 9876543210"
                    value={form.customerContact}
                    onChange={e => setForm({ ...form, customerContact: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Category</Form.Label>
                  <Form.Select
                    size="sm"
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                  >
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Sale Date</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={form.date}
                    onChange={e => setForm({ ...form, date: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Invoice / Reference No</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. INV-1002 / UPI ref"
                    value={form.invoiceRef}
                    onChange={e => setForm({ ...form, invoiceRef: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Items Table */}
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="small fw-bold text-uppercase text-muted">Items / Products Sold</span>
                <Button variant="outline-success" size="sm" className="rounded-pill px-2 py-0" onClick={addItem}>
                  <i className="fas fa-plus me-1" />
                  Add Line Item
                </Button>
              </div>

              <div className="border rounded-3 p-2 bg-light">
                {form.items.map((item, idx) => (
                  <Row className="g-2 align-items-center mb-2" key={idx}>
                    <Col md={5}>
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Item name / description"
                        value={item.name}
                        onChange={e => handleItemChange(idx, 'name', e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Control
                        type="number"
                        size="sm"
                        min="1"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={e => handleItemChange(idx, 'quantity', e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Control
                        type="number"
                        size="sm"
                        min="0"
                        step="0.01"
                        placeholder="Price"
                        value={item.unitPrice}
                        onChange={e => handleItemChange(idx, 'unitPrice', e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <span className="fw-bold small">
                        ₹{(item.totalPrice || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </Col>
                    <Col md={1} className="text-end">
                      {form.items.length > 1 && (
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0"
                          onClick={() => removeItem(idx)}
                        >
                          <i className="fas fa-trash-alt" />
                        </Button>
                      )}
                    </Col>
                  </Row>
                ))}
              </div>
            </div>

            {/* Payment Details */}
            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Payment Status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={form.paymentStatus}
                    onChange={e => {
                      const nextStatus = e.target.value;
                      const total = calculateTotal(form.items);
                      setForm({
                        ...form,
                        paymentStatus: nextStatus,
                        paidAmount: nextStatus === 'Paid' ? total : nextStatus === 'Unpaid' ? 0 : form.paidAmount,
                      });
                    }}
                  >
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Payment Mode</Form.Label>
                  <Form.Select
                    size="sm"
                    value={form.paymentMode}
                    onChange={e => setForm({ ...form, paymentMode: e.target.value })}
                  >
                    {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Amount Paid (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min="0"
                    step="0.01"
                    value={form.paidAmount}
                    onChange={e => setForm({ ...form, paidAmount: Number(e.target.value) })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Label className="small fw-semibold">Notes / Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                placeholder="Optional notes or details about this sale"
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
              />
            </Form.Group>

            {/* Total Summary */}
            <div className="p-3 rounded-3 mt-3 d-flex align-items-center justify-content-between" style={{ background: '#ecfdf5' }}>
              <div>
                <span className="text-muted small">Total Sale:</span>{' '}
                <strong className="text-dark fs-5">
                  ₹{calculateTotal(form.items).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </strong>
              </div>
              <div>
                <span className="text-muted small">Balance Due:</span>{' '}
                <strong className="text-danger fs-5">
                  ₹{Math.max(0, calculateTotal(form.items) - (Number(form.paidAmount) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </strong>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" size="sm" className="rounded-pill px-3" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              variant="success"
              size="sm"
              type="submit"
              className="rounded-pill px-4 fw-semibold"
              disabled={saving}
            >
              {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="fas fa-save me-1" />}
              {isEditing ? 'Save Changes' : 'Record Sale'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ─── Quick Payment Status Modal ─────────────────────────────────── */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold" style={{ fontSize: '1rem' }}>
            Update Payment Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Status</Form.Label>
            <Form.Select
              size="sm"
              value={quickStatus}
              onChange={e => {
                const s = e.target.value;
                setQuickStatus(s);
                if (statusSale) {
                  if (s === 'Paid') setQuickPaid(statusSale.totalAmount);
                  else if (s === 'Unpaid') setQuickPaid(0);
                }
              }}
            >
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Payment Mode</Form.Label>
            <Form.Select
              size="sm"
              value={quickPayMode}
              onChange={e => setQuickPayMode(e.target.value)}
            >
              {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold">Paid Amount (₹)</Form.Label>
            <Form.Control
              type="number"
              size="sm"
              value={quickPaid}
              onChange={e => setQuickPaid(Number(e.target.value))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleUpdateStatus}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Delete Confirmation Modal ───────────────────────────────────── */}
      <Modal show={!!confirmDeleteId} onHide={() => setConfirmDeleteId(null)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-danger" style={{ fontSize: '1rem' }}>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          Are you sure you want to delete this sale record? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setConfirmDeleteId(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={deleting}
            onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)}
          >
            {deleting ? <Spinner size="sm" animation="border" className="me-1" /> : null}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SalesTracker;
